import React, { useState } from 'react';
import { AlertCircle, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { createUnblockRequest } from '../services/unblockRequestApi';
import { toast } from 'react-hot-toast';

interface BlockedAccountModalProps {
  isOpen: boolean;
  userEmail?: string;
  userName?: string;
}

export const BlockedAccountModal: React.FC<BlockedAccountModalProps> = ({
  isOpen,
  userEmail,
  userName,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleRequestUnblock = async () => {
    setIsSubmitting(true);
    try {
      await createUnblockRequest({ reason: reason.trim() || undefined });
      setRequestSubmitted(true);
      toast.success('Unblock request submitted successfully! Your SHM and Admin will review it soon.');
    } catch (error: any) {
      console.error('Failed to submit unblock request:', error);
      toast.error(error.message || 'Failed to submit unblock request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[10000] p-4"
      style={{ pointerEvents: 'auto' }}
    >
      <div 
        className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl transform transition-all duration-300 scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center">
          {/* Icon */}
          <div className="mb-6 flex justify-center">
            <div className={`p-4 rounded-full ${requestSubmitted ? 'bg-green-100' : 'bg-red-100'}`}>
              {requestSubmitted ? (
                <CheckCircle2 className="h-12 w-12 text-green-600" />
              ) : (
                <AlertCircle className="h-12 w-12 text-red-600" />
              )}
            </div>
          </div>
          
          {/* Title */}
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            {requestSubmitted ? 'Request Submitted' : 'Account Blocked'}
          </h3>
          
          {/* Description */}
          {!requestSubmitted ? (
            <>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Your account has been blocked. Please request to unblock your account.
              </p>
              
              {/* Reason Input */}
              <div className="mb-6 text-left">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason (Optional)
                </label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Please provide a reason for unblocking your account..."
                  className="w-full min-h-[100px] resize-none"
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {reason.length}/500 characters
                </p>
              </div>
              
              {/* Request Button */}
              <Button
                onClick={handleRequestUnblock}
                disabled={isSubmitting}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 font-medium w-full sm:w-auto"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Request to Unblock
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Your unblock request has been submitted successfully! We will review your request and get back to you soon.
              </p>
             
            </>
          )}
        </div>
      </div>
    </div>
  );
};

